import { setLocale } from '@vee-validate/i18n'
import { defineStore } from 'pinia'
import { usePrimeVue } from 'primevue/config'
import { type Ref, ref } from 'vue'

import { PredefinedText } from '@/enums/common.ts'
import { isAuthenticated } from '@/libs/auth'
import dayjs from '@/libs/dayjs'
import i18n, { type i18nType } from '@/libs/i18n'
import { changePrimevueLanguage } from '@/libs/primevue/primevue.ts'
import { getEmptySellerObject } from '@/libs/seller.ts'
import { PrintError } from '@/libs/toast.ts'
import { useFetchStore } from '@/stores/fetch.ts'
import type { Seller } from '@/types/Seller.ts'

export const useAppStore = defineStore('app', () => {
    const primevue = usePrimeVue()

    const JWT: Ref<string> = ref(localStorage.getItem('JWT') || '')
    const locale: Ref<string> = ref('en')
    const windowWidth: Ref<number> = ref(0)
    const loaderActive: Ref<number> = ref(0)
    const loggedIn: Ref<boolean> = ref(isAuthenticated())
    const userData: Ref<Seller> = ref(getEmptySellerObject())

    const changeLocale = (newLocale: string) => {
        const { locale: i18nLocale } = i18n.global
        locale.value = newLocale
        i18nLocale.value = locale.value as i18nType
        dayjs.locale(locale.value)
        setLocale(locale.value)
        changePrimevueLanguage(primevue, locale.value)
    }

    const resizeHandler = (): void => {
        windowWidth.value = window.innerWidth
    }

    const isMobile = (): boolean => {
        return windowWidth.value < 1024
    }

    const startLoading = () => {
        loaderActive.value++
    }
    const stopLoading = () => {
        if (loaderActive.value > 0) loaderActive.value--
    }

    const setJWTCookie = () => {
        if (JWT.value.length > 0) document.cookie = `JWT=${JWT.value};path=/;`
        else document.cookie = 'JWT=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
    }

    const setUserData = (data: Seller) => {
        userData.value = data
        changeLocale(userData.value.language || 'en')
    }

    // getters
    const isLoaderActive = () => {
        return !!loaderActive.value
    }

    const getUserData = () => {
        return userData.value
    }

    const isLoggedIn = () => {
        return loggedIn.value
    }

    const getJWT = () => {
        return JWT.value
    }

    const loadUserData = async() => {
        if (!isLoggedIn()) return

        const { statusCode, data } = await useFetchStore().fetch('/user_data').get().json<Seller>()
        if (statusCode.value === 200 && data.value) {
            setUserData(data.value)
        } else {
            PrintError(PredefinedText.value.ErrorLoadData)
        }
    }

    const setLogin = async(jwt: string) => {
        localStorage.setItem('JWT', jwt)
        JWT.value = jwt
        setJWTCookie()
        await loadUserData()
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        userData.value = getEmptySellerObject()
        JWT.value = ''
        setJWTCookie()
    }

    return {
        windowWidth,
        changeLocale,
        isMobile,
        resizeHandler,
        setLogin,
        setLogout,
        isLoaderActive,
        startLoading,
        stopLoading,
        setUserData,
        setJWTCookie,
        getUserData,
        isLoggedIn,
        getJWT,
        loadUserData
    }
})
